import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2, X, ChevronRight, ChevronDown, Loader2, Bell, Lock, Unlock, Check, Download, FileText } from 'lucide-react';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import DriverSalarySheet, { sar } from './DriverSalarySheet';

interface EditRequest {
  id: string; scope: 'sheet' | 'month'; note: string; status: string;
  requestedAt: string; requestedBy: string | null;
  decidedAt: string | null; decidedBy: string | null; decisionNote: string | null;
  driverId: string | null; driverName: string | null;
  periodStart: string; sheetStatus: string | null;
}

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

/** Is anything in this period locked? Drives which month button is offered. */
const rowsHaveLock = (items?: { sheetStatus: string }[]) =>
  (items ?? []).some((r) => r.sheetStatus === 'submitted');

/** Whole calendar months back from today, for the range picker. */
function recentMonths(count: number): { key: string; label: string; from: string; to: string }[] {
  const out: { key: string; label: string; from: string; to: string }[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, d.getMonth() + 1, 0).getDate();
    out.push({
      key: `${y}-${m}`,
      label: d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      from: `${y}-${m}-01`,
      to: `${y}-${m}-${String(last).padStart(2, '0')}`,
    });
  }
  return out;
}

// PR-DRIVER-SALARY-SHEET — the driver list stays deliberately narrow: name,
// what they are owed. Clicking a row opens that driver's full
// salary sheet underneath it, so the detail is one click away rather than
// spread across a table nobody can read.

interface Row {
  driverId: string; driverCode: string; name: string; iqama: string | null;
  tripCount: number; totalKm: number;
  lifetimeLoads: number;
  hasPayConfig: boolean;
  basicSalarySar: number | null;
  perKmRateSar: number | null;
  perTripRateSar: number | null;
  totalAmountSar: number;
  deductionsSar: number;
  netSalarySar: number;
  receivedSar: number;
  pendingSar: number;
  sheetSaved: boolean;
  sheetStatus: 'draft' | 'submitted';
  hasPendingRequest: boolean;
}

const dateCls = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

/** First day of the current month, which is what a salary sheet usually covers. */
function monthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/** What each side is called on screen. */
const SIDE_LABEL: Record<'dumpers' | 'neom', string> = { dumpers: 'Dumpers', neom: 'NEOM' };

export default function DumperDriverPayPage({ side = 'dumpers' }: { side?: 'dumpers' | 'neom' } = {}) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState('');            // optional: blank means "to today"

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dumper-pay-list', side, from, to],
    enabled: !!from,
    queryFn: async () =>
      (await apiClient.get<{ period: { from: string; to: string; openEnded: boolean }; items: Row[] }>(
        `/api/v1/dumpers/driver-pay/sheet/list?side=${side}`,
        { params: { from, ...(to ? { to } : {}) } },
      )).data,
  });

  // One open row at a time: two sheets side by side in an accordion is noise,
  // and each one is a request.
  const [openId, setOpenId] = useState<string | null>(null);

  // ---- edit requests. The server decides what this user may see: a super
  // admin gets every open ask, everyone else only their own.
  const { data: requests } = useQuery({
    queryKey: ['salary-edit-requests'],
    queryFn: async () =>
      (await apiClient.get<{ canDecide: boolean; items: EditRequest[] }>(
        `/api/v1/dumpers/driver-pay/edit-requests?side=${side}`,
      )).data,
    refetchInterval: 60_000,
  });
  const [inboxOpen, setInboxOpen] = useState(false);
  const [decisionNote, setDecisionNote] = useState<Record<string, string>>({});
  const pendingCount = requests?.items.length ?? 0;

  const isSuper = useAuth((s) => s.user?.role) === 'super_admin';
  const anyLocked = rowsHaveLock(data?.items);
  const monthRequestPending = (requests?.items ?? []).some(
    (rq) => rq.scope === 'month' && rq.status === 'pending' && rq.periodStart === from,
  );

  // ---- lock / unlock the whole period
  const [askMonthOpen, setAskMonthOpen] = useState(false);
  const [monthNote, setMonthNote] = useState('');
  const [monthMsg, setMonthMsg] = useState<string | null>(null);

  const monthMut = useMutation({
    mutationFn: async (action: 'lock' | 'unlock') =>
      apiClient.post(`/api/v1/dumpers/driver-pay/month/${action}?side=${side}`, { from, to: to || null }),
    onSuccess: (res, action) => {
      const d = res.data as { sheetsLocked?: number; sheetsCreated?: number; sheetsUnlocked?: number };
      setMonthMsg(action === 'lock'
        ? `Locked ${d.sheetsLocked ?? 0} sheet(s)${d.sheetsCreated ? `, ${d.sheetsCreated} created` : ''}.`
        : `Unlocked ${d.sheetsUnlocked ?? 0} sheet(s).`);
      qc.invalidateQueries({ queryKey: ['dumper-pay-list'] });
      qc.invalidateQueries({ queryKey: ['dumper-pay-sheet'] });
      qc.invalidateQueries({ queryKey: ['salary-edit-requests'] });
    },
    onError: (e) => setMonthMsg(extractErrorMessage(e)),
  });

  const askMonthMut = useMutation({
    mutationFn: async () => {
      if (!monthNote.trim()) throw new Error('Say why the month needs unlocking.');
      return apiClient.post(`/api/v1/dumpers/driver-pay/month/request-unlock?side=${side}`, { from, note: monthNote.trim() });
    },
    onSuccess: () => {
      setAskMonthOpen(false); setMonthNote('');
      setMonthMsg('Request sent. A super admin will review it.');
      qc.invalidateQueries({ queryKey: ['salary-edit-requests'] });
    },
    onError: (e) => setMonthMsg(e instanceof Error && e.message.startsWith('Say why') ? e.message : extractErrorMessage(e)),
  });

  // Throw away one driver's saved sheet for this period. Super admin only, and
  // the server checks it again - a button hidden in the browser is a decoration.
  const [deleting, setDeleting] = useState<string | null>(null);
  const deleteSheetMut = useMutation({
    mutationFn: async (driverId: string) =>
      apiClient.delete(`/api/v1/dumpers/driver-pay/sheet?side=${side}`, { data: { driverId, from } }),
    onSuccess: () => {
      setDeleting(null);
      setMonthMsg('Sheet deleted.');
      qc.invalidateQueries({ queryKey: ['dumper-pay-list'] });
      qc.invalidateQueries({ queryKey: ['dumper-pay-sheet'] });
      qc.invalidateQueries({ queryKey: ['salary-edit-requests'] });
    },
    onError: (e) => { setDeleting(null); setMonthMsg(extractErrorMessage(e)); },
  });

  // Unlock one driver without going through a request. Super admin only.
  const unlockOneMut = useMutation({
    mutationFn: async (driverId: string) =>
      apiClient.post(`/api/v1/dumpers/driver-pay/sheet/unlock?side=${side}`, { driverId, from }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dumper-pay-list'] });
      qc.invalidateQueries({ queryKey: ['dumper-pay-sheet'] });
      qc.invalidateQueries({ queryKey: ['salary-edit-requests'] });
    },
    onError: (e) => setMonthMsg(extractErrorMessage(e)),
  });

  // ---- downloads. The file is behind auth, so fetch it as a blob rather than
  // pointing the browser at a URL it cannot authorise.
  const [downloading, setDownloading] = useState<string | null>(null);
  const [pickMonth, setPickMonth] = useState(false);
  async function download(format: 'xlsx' | 'pdf', rangeFrom: string, rangeTo: string) {
    const key = `${format}:${rangeFrom}`;
    setDownloading(key); setMonthMsg(null);
    try {
      const jwt = getStoredToken();
      // The side belongs here too, or the NEOM page downloads dumper pay.
      const qs = new URLSearchParams({ from: rangeFrom, to: rangeTo, format, side });
      const res = await fetch(`${API_BASE}/api/v1/dumpers/driver-pay/export?${qs}`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `driver-salary-${rangeFrom}_to_${rangeTo}.${format}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setMonthMsg(extractErrorMessage(e));
    } finally {
      setDownloading(null);
    }
  }

  const decideMut = useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) =>
      apiClient.post(`/api/v1/dumpers/driver-pay/edit-requests/${id}/decide?side=${side}`, {
        approve, note: decisionNote[id]?.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['salary-edit-requests'] });
      qc.invalidateQueries({ queryKey: ['dumper-pay-sheet'] });
      qc.invalidateQueries({ queryKey: ['dumper-pay-list'] });
    },
  });

  const rows = data?.items ?? [];
  const totalNet = rows.reduce((a, r) => a + r.netSalarySar, 0);
  const totalPending = rows.reduce((a, r) => a + r.pendingSar, 0);

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          {/* The page serves both sides; a fixed "Dumpers" title on the NEOM
              page is how somebody reads the wrong figures with confidence. */}
          <div className="font-display text-lg">{SIDE_LABEL[side]} · Driver salary</div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" className={dateCls} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Start date" />
            <span className="text-muted-foreground text-sm">→</span>
            <input type="date" className={dateCls} value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-label="End date (optional)" />
            {to && (
              <button type="button" className="text-xs underline text-muted-foreground hover:text-foreground" onClick={() => setTo('')}>
                clear
              </button>
            )}
            {/* The inbox only appears when there is something in it, so the
                header stays quiet on the ordinary day. */}
            {pendingCount > 0 && (
              <button
                type="button"
                onClick={() => setInboxOpen((v) => !v)}
                className="relative inline-flex items-center gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 px-2.5 h-9 text-sm text-amber-700 hover:bg-amber-500/20 transition-colors"
                aria-label={`${pendingCount} edit request${pendingCount === 1 ? '' : 's'}`}
              >
                <Bell className="h-4 w-4" />
                {requests?.canDecide ? 'Requests' : 'My request'}
                <span className="inline-flex items-center justify-center min-w-5 h-5 rounded-full bg-amber-600 text-white text-[11px] px-1">
                  {pendingCount}
                </span>
              </button>
            )}
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard'; }} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> {SIDE_LABEL[side]} home
            </Button>
          </div>
        </div>

        {inboxOpen && pendingCount > 0 && (
          <div className="border-t border-border bg-amber-500/5">
            <div className="max-w-[1600px] mx-auto px-6 py-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  {requests?.canDecide ? 'Edit requests waiting on you' : 'Your edit request'}
                </div>
                <button type="button" onClick={() => setInboxOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close requests">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {(requests?.items ?? []).map((rq) => (
                <div key={rq.id} className="rounded-lg border border-border bg-card p-3 space-y-2">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                    <div>
                      {rq.scope === 'month' ? (
                        <><b>Whole period</b><span className="text-muted-foreground"> from {rq.periodStart} · every driver</span></>
                      ) : (
                        <><b>{rq.driverName}</b><span className="text-muted-foreground"> · period from {rq.periodStart}</span></>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {rq.requestedBy ?? 'unknown'} · {rq.requestedAt.slice(0, 16).replace('T', ' ')}
                    </div>
                  </div>
                  <p className="text-sm rounded bg-muted/40 px-3 py-2">{rq.note}</p>
                  {requests?.canDecide ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        className="h-9 flex-1 min-w-40 rounded-md border border-input bg-background px-2 text-sm"
                        value={decisionNote[rq.id] ?? ''}
                        onChange={(e) => setDecisionNote((p) => ({ ...p, [rq.id]: e.target.value }))}
                        placeholder="note back (optional)"
                        aria-label="Decision note"
                      />
                      <Button size="sm" variant="gold" className="gap-1"
                        disabled={decideMut.isPending}
                        onClick={() => decideMut.mutate({ id: rq.id, approve: true })}>
                        <Check className="h-3.5 w-3.5" /> Approve &amp; reopen
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1"
                        disabled={decideMut.isPending}
                        onClick={() => decideMut.mutate({ id: rq.id, approve: false })}>
                        <X className="h-3.5 w-3.5" /> Reject
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Waiting on a super admin.</p>
                  )}
                </div>
              ))}
              {decideMut.isError && (
                <p className="text-sm text-destructive" role="alert">{extractErrorMessage(decideMut.error)}</p>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-[1600px] mx-auto w-full px-6 py-8">
        <p className="text-sm text-muted-foreground mb-4">
          Only drivers who worked in the chosen dates are listed. Click one to open their sheet.
          The end date is optional — leave it blank and the period runs to today.
          {data && <> Showing <b className="text-foreground">{data.period.from}</b> to <b className="text-foreground">{data.period.to}</b>{data.period.openEnded && ' (open)'}.</>}
        </p>

        {isError && (
          <p className="mb-3 text-sm text-destructive rounded border border-destructive/30 bg-destructive/10 px-3 py-2" role="alert">
            {extractErrorMessage(error)}
          </p>
        )}

        {/* -------- lock the period, and take it away as a file -------- */}
        <div className="rounded-xl border border-border bg-card p-3 mb-4 flex flex-wrap items-center gap-2">
          {isSuper ? (
            <>
              <Button size="sm" variant={anyLocked ? 'outline' : 'gold'} className="gap-1"
                disabled={monthMut.isPending || rows.length === 0}
                onClick={() => monthMut.mutate('lock')}>
                {monthMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                Lock the whole period
              </Button>
              {anyLocked && (
                <Button size="sm" variant="outline" className="gap-1"
                  disabled={monthMut.isPending}
                  onClick={() => monthMut.mutate('unlock')}>
                  <Unlock className="h-4 w-4" /> Unlock the whole period
                </Button>
              )}
            </>
          ) : (
            anyLocked && (
              monthRequestPending ? (
                <span className="text-sm text-amber-700 inline-flex items-center gap-1.5">
                  <Bell className="h-4 w-4" /> Your unlock request for this period is with a super admin.
                </span>
              ) : (
                <Button size="sm" variant="outline" className="gap-1" onClick={() => setAskMonthOpen((v) => !v)}>
                  <Unlock className="h-4 w-4" /> Request unlock for the period
                </Button>
              )
            )
          )}

          <div className="ms-auto flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Download this period</span>
            <Button size="sm" variant="outline" className="gap-1"
              disabled={downloading !== null || rows.length === 0}
              onClick={() => download('xlsx', from, data?.period.to ?? from)}>
              {downloading === `xlsx:${from}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Excel
            </Button>
            <Button size="sm" variant="outline" className="gap-1"
              disabled={downloading !== null || rows.length === 0}
              onClick={() => download('pdf', from, data?.period.to ?? from)}>
              {downloading === `pdf:${from}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setPickMonth((v) => !v)}>
              or pick a month…
            </Button>
          </div>

          {/* Download a month other than the one on screen, without disturbing
              the dates the page is showing. */}
          {pickMonth && (
            <div className="w-full border-t border-border pt-3 mt-1">
              <div className="text-xs text-muted-foreground mb-2">Pick a month to download</div>
              <div className="flex flex-wrap gap-2">
                {recentMonths(12).map((m) => (
                  <div key={m.key} className="inline-flex items-center rounded-md border border-border overflow-hidden">
                    <span className="px-2 py-1 text-xs">{m.label}</span>
                    <button type="button"
                      className="px-2 py-1 text-xs border-s border-border hover:bg-muted/40 disabled:opacity-50"
                      disabled={downloading !== null}
                      onClick={() => download('xlsx', m.from, m.to)}>
                      {downloading === `xlsx:${m.from}` ? '…' : 'Excel'}
                    </button>
                    <button type="button"
                      className="px-2 py-1 text-xs border-s border-border hover:bg-muted/40 disabled:opacity-50"
                      disabled={downloading !== null}
                      onClick={() => download('pdf', m.from, m.to)}>
                      {downloading === `pdf:${m.from}` ? '…' : 'PDF'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {askMonthOpen && !isSuper && (
            <div className="w-full border-t border-border pt-3 mt-1 space-y-2">
              <label className="text-xs text-muted-foreground" htmlFor="month-note">
                Why does this period need unlocking? A super admin reads this before deciding.
              </label>
              <textarea id="month-note" rows={2} maxLength={2000}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={monthNote} onChange={(e) => { setMonthNote(e.target.value); setMonthMsg(null); }}
                placeholder="e.g. the Aldrees invoice for the month landed after we closed it" />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setAskMonthOpen(false); setMonthNote(''); }}>Cancel</Button>
                <Button variant="gold" size="sm" onClick={() => askMonthMut.mutate()} disabled={askMonthMut.isPending} className="gap-1">
                  {askMonthMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Send request
                </Button>
              </div>
            </div>
          )}

          {monthMsg && (
            <p className={`w-full text-sm ${monthMsg.startsWith('Locked') || monthMsg.startsWith('Unlocked') || monthMsg.startsWith('Request sent') ? 'text-emerald-600' : 'text-destructive'}`}>
              {monthMsg}
            </p>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="text-start font-medium px-4 py-2">Driver</th>
                <th className="text-end font-medium px-4 py-2">Total pay</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin inline me-2" /> Loading…
                </td></tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                  No driver worked in this period. Widen the dates, or log a load first.
                </td></tr>
              )}
              {rows.map((r) => {
                const open = openId === r.driverId;
                return (
                  // Fragment needs the key: a row can render up to three <tr>s.
                  <Fragment key={r.driverId}>
                    <tr
                      className={`border-t border-border cursor-pointer transition-colors ${open ? 'bg-muted/40' : 'hover:bg-muted/20'}`}
                      onClick={() => setOpenId(open ? null : r.driverId)}
                    >
                      <td className="px-3 py-2 text-muted-foreground">
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="px-4 py-2">
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {r.driverCode}
                          {/* Three drivers here are called Naeem Abbas. The
                              iqama is the only thing that separates them. */}
                          {r.iqama && <span className="ms-2">{r.iqama}</span>}
                          {r.tripCount > 0 && <span className="ms-2 font-sans">{r.tripCount} trips</span>}
                          {!r.hasPayConfig && <span className="ms-2 font-sans text-amber-600">no pay set</span>}
                          {r.sheetStatus === 'submitted' && (
                            <span className="ms-2 font-sans inline-flex items-center gap-1"><Lock className="h-3 w-3" /> submitted</span>
                          )}
                          {r.hasPendingRequest && (
                            <span className="ms-2 font-sans text-amber-600">edit requested</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-end">
                        <div className="font-mono font-medium">{sar(r.netSalarySar)}</div>
                        {r.pendingSar !== 0 && (
                          <div className="text-[11px] text-amber-600 font-mono">{sar(r.pendingSar)} pending</div>
                        )}
                      </td>
                      {/* The row toggles the sheet, so the controls must not. */}
                      <td className="px-4 py-2 text-end whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          {isSuper && r.sheetStatus === 'submitted' && (
                            <Button size="sm" variant="ghost" className="gap-1"
                              disabled={unlockOneMut.isPending}
                              onClick={() => unlockOneMut.mutate(r.driverId)}>
                              <Unlock className="h-3.5 w-3.5" /> Unlock
                            </Button>
                          )}
                          {/* Only offered where there is something to delete: a
                              sheet that was never saved has nothing to throw
                              away, and the server would answer 404. */}
                          {isSuper && r.sheetSaved && r.sheetStatus !== 'submitted' && (
                            deleting === r.driverId ? (
                              <span className="inline-flex items-center gap-1">
                                <span className="text-[11px] text-muted-foreground">Delete this sheet?</span>
                                <Button size="sm" variant="ghost" className="gap-1 text-destructive"
                                  disabled={deleteSheetMut.isPending}
                                  onClick={() => deleteSheetMut.mutate(r.driverId)}>
                                  {deleteSheetMut.isPending
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Trash2 className="h-3.5 w-3.5" />} Yes
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDeleting(null)}>No</Button>
                              </span>
                            ) : (
                              <Button size="sm" variant="ghost" className="gap-1 text-destructive"
                                title="Delete the typed figures for this driver and period"
                                onClick={() => { setDeleting(r.driverId); setMonthMsg(null); }}>
                                <Trash2 className="h-3.5 w-3.5" /> Delete
                              </Button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>

                    {open && (
                      <tr>
                        <td colSpan={4} className="p-0 border-t border-border">
                          <DriverSalarySheet side={side} driverId={r.driverId} from={from} to={to} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-border bg-muted/20">
                  <td className="px-3 py-2"></td>
                  <td className="px-4 py-2 font-medium">Total ({rows.length} drivers)</td>
                  <td className="px-4 py-2 text-end">
                    <div className="font-mono font-semibold">{sar(totalNet)}</div>
                    {totalPending !== 0 && (
                      <div className="text-[11px] text-amber-600 font-mono">{sar(totalPending)} pending</div>
                    )}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </main>
    </div>
  );
}
